import timeWarningSound from "assets/time_warning.mp3";
import { logSession, logError, LogCategory } from "utils/sentryLogger";

// Safari grants autoplay per media element, so a fresh `new Audio()` per warning
// is blocked (NotAllowedError) whenever the student hasn't interacted recently.
// One shared element, played once inside a user gesture, stays allowed for
// every later programmatic play.
let chime;
const getChime = () => (chime ??= new Audio(timeWarningSound));

let chimeUnlocked = false;
const unlockChime = () => {
  if (chimeUnlocked) return;
  chimeUnlocked = true;
  const el = getChime();
  // Already sounding means already allowed; don't cut a real warning short.
  if (el.paused === false) return;
  // The play() call inside the gesture is what lifts Safari's restriction on
  // this element; pausing synchronously means no audio is ever rendered. A
  // muted play was tried first: iPad Safari still let the chime through and
  // did not grant later unmuted plays.
  // Promise.resolve: play() returns undefined in jsdom, so any test that
  // presses a key after importing this module would otherwise throw here.
  Promise.resolve(el.play()).catch(() => {});
  el.pause();
  el.currentTime = 0;
};

// Armed at load and again whenever a real play is refused, so the student's
// next tap re-unlocks the element instead of every later warning staying silent.
const armChimeUnlock = () => {
  if (typeof document === "undefined") return;
  chimeUnlocked = false;
  document.addEventListener("pointerdown", unlockChime, { capture: true, once: true });
  document.addEventListener("keydown", unlockChime, { capture: true, once: true });
};
armChimeUnlock();

const dataTrackDefault = {
  event_type: null,
  data: {
    is_more_time_request: null,
    time_remaining_seconds: null,
  },
}

export const timeWarningHandler = ({
  dataTrackEvent = dataTrackDefault,
  timeTrack,
  setTimerValue,
  setTimeTrack,
  setIsShowRequestMoreTimeButton,
  setIsMoreTimeRequested,
}) => {
  const { event_type, data } = dataTrackEvent;
  const { is_more_time_request, time_remaining_seconds } = data ?? {};
  if (event_type === "time_warning") {
    if (is_more_time_request) {
      const newTotalTime = timeTrack + time_remaining_seconds;
      setTimerValue(newTotalTime);
      setTimeTrack(newTotalTime);
    } else {
      setTimerValue(time_remaining_seconds);
      setTimeTrack(time_remaining_seconds);
      // Log play success/failure so we can confirm the alert actually sounds in
      // production (autoplay policy can silently block `.play()`).
      const el = getChime();
      el.currentTime = 0;
      el.play()
        .then(() => {
          logSession("Time warning sound played", {
            event: "time_warning_sound",
            timeRemainingSeconds: time_remaining_seconds,
          });
        })
        .catch((error) => {
          logError("Time warning sound failed to play", error, {
            category: LogCategory.SESSION,
            event: "time_warning_sound_failed",
            timeRemainingSeconds: time_remaining_seconds,
          });
          armChimeUnlock();
        });
    }

    setIsShowRequestMoreTimeButton(!is_more_time_request)
    setIsMoreTimeRequested(is_more_time_request);
  }

}
